<script>
    import {onDestroy, onMount} from 'svelte';
    import {Route, navigateTo} from 'svelte-router-spa';
    import {PRConnect} from "@projectroom/ui-lib";
    import {messaging} from "./store/messaging-store";
	import { planStore } from "./store/plan-store";
	import { ThemeToggle, Dialog, Button, Input } from "./../lib/ui";
	import AccountMenu from "./common/AccountMenu.svelte";

    export let currentRoute;
    export let params = {};
    let messagingSubsc;
    let dropdownLink = false;
    let popupPosting = false;
    let popupDisposal = false;
    let isLatestUpdated = false;
    let pageUrl;
    let qrUrl;
    let successMesage;
    let botTitle = "";
    let autoSaveFlag = false;
	let isLoading;
	let botId;
	let isLoaded;
	let publishOpen = false;

    onMount(() => {
        messagingSubsc = messaging.subscribe(message => {
            if (message.target !== "BuilderLayout") return;
            // 에디터 sticky 툴바(Builder.svelte)에서 올라온 다이얼로그 열기 요청
            if (message.action === "openPublish") { publishOpen = true; return; }
            if (message.action === "openDisposal") { if (isLatestUpdated) popupDisposal = true; return; }
            // 데이터 로드/저장 후 상태 갱신(bot 포함 메시지)
            if (message.bot) {
                isLatestUpdated = message.bot.isLatestUpdated();
                pageUrl = message.bot.pageUrl;
				qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${pageUrl}&amp;size=100x100`;
                botTitle = message.bot.title;
                handleAutoSave();
				isLoaded = true;
            }
        });
		botId = currentRoute.namedParams.botId;
    });
     
    onDestroy(() => {
        messagingSubsc();
    });

    // 게시 진행 중 재클릭으로 인한 중복 postChangeBotData 호출을 막는 in-flight 가드
    let posting = false;
    const clickPosting = () => {
        if (posting) return;
        posting = true;
        popupPosting = false;
        dropdownLink = true;
        PRConnect.send("/api/Post/postChangeBotData", {botId}, data => {
            posting = false;
            if( data.header.status === "success") {
                console.log("postChangeBotData success");
                // 게시 완료 → 팝업 닫기(성공 피드백 + 재클릭 중복 방지)
                // & 폐기 가드 최신화 + 에디터 sticky에 게시 완료 통지(미반영 점 끄기)
                publishOpen = false;
                isLatestUpdated = false;
                messaging.set({ target: "Builder", action: "published" });
            }
        });
    }

    const clickDisposal = () => {
        popupDisposal = false;
        PRConnect.send("/api/Post/disposalChangeBotData", {botId}, data => {
            if( data.header.status === "success") {
                console.log("disposalChangeBotData success", data);
                location.reload();
            }
        });
    }

    const clickOutLayer = () => {
        dropdownLink = false;
        popupPosting = false;
        popupDisposal = false;
    }

    const clickDisposalButton = () => {
        if (!isLatestUpdated) {
            return;
        }
        popupDisposal = true
    }

    const clickCopy = () => {
        navigator.clipboard.writeText(pageUrl).then(
            () => {
                // successMesage = "clipboard에 복사되었습니다.";
                console.log("clipboard 복사 성공");
            },
        );
    }

    const handleAutoSave = () => {
        autoSaveFlag = true;
        setTimeout(()=> {
            autoSaveFlag = false;
        }, 1500);
    }

	const clickRefresh = () => {
		isLoading = true;
		PRConnect.send("/api/Bot/updateBotQrUrl", {botId}, data => {
            if( data.header.status === "success") {
                isLoading = false;
				qrUrl = data.result;
            }
        });
	}

</script>

<div class="tw-min-h-screen tw-bg-background tw-text-foreground">
	<!-- shadcn 톱바(ChatBuilderLayout와 통일): 좌 로고/제목 · 우 액션 + 테마토글 + 프로필. fixed 56px. -->
	<header
		class="builder-topbar tw-fixed tw-inset-x-0 tw-top-0 tw-z-50 tw-flex tw-h-14 tw-items-center tw-justify-between tw-border-b tw-border-border tw-bg-sidebar tw-font-sans" style="padding-inline: max(1rem, calc((100vw - var(--app-max-w, 1920px)) / 2))"
	>
		<div class="tw-flex tw-items-center tw-gap-2.5">
			<button
				type="button"
				class="tw-flex tw-h-8 tw-w-8 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-transition-colors hover:tw-bg-muted"
				on:click={() => navigateTo("/main")}
				aria-label="아바톡 홈"
				title="아바톡 홈"
			>
				<img class="tw-h-6 tw-w-6" src="/static/images/avatalk-charecter-pink.svg" alt="아바톡" />
			</button>
			<div class="tw-h-4 tw-w-px tw-bg-border"></div>
			<div class="tw-flex tw-items-center tw-gap-1.5 tw-text-sm">
				<span class="tw-font-semibold tw-text-foreground">{botTitle || "아바톡"}</span>
				<span class="tw-text-muted-foreground">/ 빌더</span>
			</div>
		</div>
		<div class="tw-flex tw-items-center tw-gap-2">
			<!-- 변경사항 버리기 / 게시하기 액션은 에디터 sticky 툴바(Builder.svelte)로 이동 → ChatBuilder와 통일.
			     이 헤더는 로고/제목 · 테마토글 · 프로필만 담당. 게시/폐기 다이얼로그는 아래에서 소유(messaging으로 열림). -->
			<ThemeToggle />
			<div class="tw-h-4 tw-w-px tw-bg-border"></div>
			<AccountMenu showEmail={false} />
		</div>
	</header>

	<Route {currentRoute} {params} />

	<!-- 공유(게시) 설정 Dialog -->
	<Dialog open={publishOpen} title="게시하기" on:close={() => (publishOpen = false)}>
		<div class="tw-space-y-4 tw-font-sans">
			<section class="tw-space-y-2.5">
				<div class="tw-flex tw-items-center tw-justify-between tw-gap-2">
					<h3 class="tw-text-sm tw-font-semibold tw-text-foreground">아바톡 페이지 URL</h3>
					<Button variant="ghost" size="sm" on:click={() => window.open(pageUrl, "_blank")} disabled={!pageUrl}>
						페이지 열기
					</Button>
				</div>
				<div class="tw-flex tw-items-center tw-gap-2">
					<Input type="text" class="tw-flex-1" value={pageUrl ?? ""} readonly aria-label="아바톡 페이지 URL" />
					<Button variant="outline" size="sm" on:click={clickCopy} disabled={!pageUrl}>복사</Button>
				</div>
				{#if qrUrl}
					<div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pt-1">
						<h3 class="tw-text-sm tw-font-semibold tw-text-foreground">아바톡 페이지 QR</h3>
						<Button variant="ghost" size="sm" on:click={clickRefresh}>불러오기</Button>
					</div>
					<div class="tw-flex tw-justify-center tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-p-3">
						<img class="tw-rounded tw-bg-white tw-p-1.5" src={qrUrl} width="100" height="100" alt="아바톡 페이지 QR" />
					</div>
				{/if}
			</section>
		</div>
		<svelte:fragment slot="footer">
			<Button variant="outline" on:click={() => (publishOpen = false)}>닫기</Button>
			<Button on:click={clickPosting}>변경사항 반영</Button>
		</svelte:fragment>
	</Dialog>

	<!-- 변경사항 폐기 확인 Dialog -->
	<Dialog open={popupDisposal} title="변경사항을 버릴까요?" on:close={() => (popupDisposal = false)}>
		<p class="tw-font-sans tw-text-sm tw-leading-relaxed tw-text-muted-foreground">
			버리면 이 페이지에서 작업한 변경내역이 사라지고, 마지막으로 게시한 상태로 돌아갑니다.
		</p>
		<svelte:fragment slot="footer">
			<Button variant="outline" on:click={() => (popupDisposal = false)}>취소</Button>
			<Button variant="destructive" on:click={clickDisposal}>변경사항 버리기</Button>
		</svelte:fragment>
	</Dialog>
</div>

<style>
	/* 톱바는 fixed(56px) → 새 빌더 셸(Builder.svelte)에서 margin-top:56px로 밀어 겹침 방지 */
	.builder-topbar {
		height: 56px;
	}
</style>
